import { DateTime, Interval } from "luxon";
import { PintValidator } from "./PintValidator";
import { PintTimeInWindowValidator } from "./PintTimeInWindowValidator";
import { dateTimeToTimeWithPint, parseTimeInPint } from "@shared/common/utils/luxonHelpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Required } from "@tractie/components";

interface PintInputProps {
  readonly label: string;
  readonly name?: string;
  readonly initialValue: DateTime<true>;
  readonly date: DateTime<true>;
  readonly window: Interval<true>;
  readonly onValueChange?: (newTime?: DateTime<true>) => void;
}

type HandleModelValueChangeEvent = CustomEvent<{
  isTriggeredByUser: boolean;
  formPath: { name: string; modelValue: string }[];
}>;

export function PintInput({
  label,
  name,
  initialValue,
  date,
  window,
  onValueChange,
}: PintInputProps) {
  const pintTimeInWindowValidator = useMemo(
    () => new PintTimeInWindowValidator(window, date),
    [window, date],
  );
  const [value, setValue] = useState<string>(dateTimeToTimeWithPint(initialValue));

  useEffect(() => {
    const newTime = parseTimeInPint(value, date);
    if (newTime && pintTimeInWindowValidator.isValidTime(newTime)) {
      onValueChange?.(newTime);
    } else {
      onValueChange?.(undefined);
    }
  }, [value, pintTimeInWindowValidator, date, onValueChange]);

  const handleModelValueChanged = useCallback(
    (event: HandleModelValueChangeEvent) => {
      if (event.detail.isTriggeredByUser) {
        setValue(event.detail.formPath[0].modelValue);
      }
    },
    [setValue],
  );

  return (
    <tractie-input
      modelValue={value}
      onmodel-value-changed={handleModelValueChanged}
      label={label}
      name={name ?? label}
      type="string"
      validators={[
        new Required(undefined, { getMessage: () => `${label} mag niet leeg zijn` }),
        new PintValidator(undefined, {
          getMessage: () => `${label} is niet goed ingevoerd (00:00.0)`,
        }),
        pintTimeInWindowValidator,
      ]}
    ></tractie-input>
  );
}
